import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { CAREER_MODES, CAREER_MODE_META } from '../utils/careerModes';

// Phase C.1a — the new Football for Nerds platform root landing page.
// Replaces the old NFL analytics/fantasy experience at `/` (see
// LandingPage.js's own now-unrouted file for that prior content —
// deliberately NOT deleted, per this phase's explicit "no wholesale
// deletion" instruction, just no longer mounted at `/`). This page
// introduces the PLATFORM (Programs/Teams/Playbook/measurables), not
// NFL stats — reuses the navy/green visual language already established
// by the real product surfaces built in C.1 (ProgramDashboardPage.js,
// ManageProgramPage.js, etc.), not LandingPage.js's own amber "glassCard"
// stat-site look, since that palette IS this platform's own identity now.

// Phase C.1c — FIVE pillars (SHOWCASE added), copy shortened to one
// concise phrase each so five cards fit in one compact row instead of
// the old 2x2 sentence-length layout. MEASURE stays "capture/track the
// data"; SHOWCASE is deliberately the separate, distinct concept of
// "present/share the results" — see SHOWCASE's own body copy below and
// the phase brief's own explicit "Measure = capture, Showcase = present"
// distinction. No backend for Showcase exists yet — this is branding/
// information architecture only, same "vision, not a shipped feature"
// framing the "Built for real programs" section below already uses.
const PILLARS = [
  {
    key: 'communicate',
    emoji: '📣',
    title: 'COMMUNICATE',
    body: 'Keep your program connected.',
  },
  {
    key: 'measure',
    emoji: '📏',
    title: 'MEASURE',
    body: 'Track what matters.',
  },
  {
    key: 'develop',
    emoji: '💪',
    title: 'DEVELOP',
    body: 'Build better athletes.',
  },
  {
    key: 'understand',
    emoji: '🧠',
    title: 'UNDERSTAND',
    body: 'Teach and learn the game.',
  },
  {
    key: 'showcase',
    emoji: '🏆',
    title: 'SHOWCASE',
    body: 'Share progress and results.',
  },
];

// The five-word primary tagline, shared by the hero heading and (kept in
// sync with it) anywhere else this exact phrase needs to appear.
const TAGLINE = 'COMMUNICATE. MEASURE. DEVELOP. UNDERSTAND. SHOWCASE.';

// Phase C.1c — VERTICAL COMPACTION. Every padding/margin value below was
// reduced from its C.1a original specifically so a ~1024px-wide
// landscape-tablet viewport can show hero + all five pillars + the
// Career Mode heading without scrolling (see the phase's own explicit
// "tablet-landscape is now a primary design constraint"). Nothing about
// the hero's CONTENT changed beyond the tagline gaining ", SHOWCASE."
function Hero({ onFindProgram, onCreateProgram }) {
  return (
    <Box sx={{
      textAlign: 'center',
      pt: { xs: 3.5, md: 4.5 },
      pb: { xs: 3, md: 3.5 },
      px: 2,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(46,168,75,0.12) 0%, transparent 70%)',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography variant="h1" sx={{
          fontWeight: 900,
          lineHeight: 1.05,
          mb: 1.25,
          letterSpacing: '-0.03em',
          fontSize: { xs: '2rem', sm: '2.8rem', md: '3.4rem' },
        }}>
          <span style={{ color: '#2ea84b' }}>Football</span>
          {' '}
          <span style={{ color: '#e8f0f8' }}>for Nerds</span>
        </Typography>

        {/* Five words now, not four ("...UNDERSTAND. SHOWCASE.") — font-
            size/letter-spacing shrunk from C.1a's original at every
            breakpoint specifically so this stays on ONE line at a
            realistic ~1024px tablet-landscape width (this phase's own
            explicit reference point) instead of wrapping awkwardly.
            Deliberately NOT `whiteSpace: 'nowrap'` — if this estimate is
            ever slightly off on a real device, normal wrapping is a safe
            two-line fallback, whereas forced nowrap could instead cause
            horizontal overflow, which this phase explicitly forbids. */}
        <Typography sx={{
          fontWeight: 800,
          letterSpacing: { xs: '0.01em', sm: '0.02em', md: '0.035em' },
          color: '#9eb8d0',
          fontSize: { xs: '0.68rem', sm: '0.85rem', md: '1.05rem', lg: '1.15rem' },
          mb: 1.5,
          px: { xs: 1, md: 0 },
        }}>
          {TAGLINE}
        </Typography>

        <Typography sx={{
          color: '#c0d4e8',
          fontSize: { xs: '0.92rem', md: '1.05rem' },
          mb: 2.5,
          maxWidth: 560,
          mx: 'auto',
          lineHeight: 1.5,
        }}>
          Build better football programs. Develop better football players.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained" color="primary" size="large"
            onClick={onCreateProgram}
            sx={{ textTransform: 'none', fontWeight: 800, px: 3.5, py: 1 }}
          >
            Create a Program
          </Button>
          <Button
            variant="outlined" size="large"
            onClick={onFindProgram}
            sx={{ textTransform: 'none', fontWeight: 700, px: 3.5, py: 1, color: '#9eb8d0', borderColor: '#2a3a4e' }}
          >
            Find Your Program
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// Phase C.1c — compacted significantly from C.1a's original (py 3.5 ->
// 1.5, emoji 2rem -> 1.35rem, one short phrase instead of a full
// sentence) so five of these read as "a concise brand framework, not
// five giant product panels" (phase brief) and fit in one row at
// tablet-landscape width. Icons/graphics themselves are UNCHANGED —
// only size/spacing moved, per this phase's explicit "graphics
// improvement deferred to a later pass."
function PillarCard({ pillar }) {
  return (
    <Card sx={{
      backgroundColor: 'rgba(10,20,34,0.8)',
      border: '1px solid #1e2a3a',
      height: '100%',
      transition: 'border-color 0.2s, transform 0.2s',
      '&:hover': { borderColor: 'rgba(46,168,75,0.4)', transform: 'translateY(-2px)' },
    }}>
      <CardContent sx={{ textAlign: 'center', py: 1.5, px: 1, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: '1.35rem', mb: 0.5, lineHeight: 1 }}>{pillar.emoji}</Typography>
        <Typography sx={{ fontWeight: 900, letterSpacing: '0.03em', color: '#2ea84b', fontSize: '0.72rem', mb: 0.5 }}>
          {pillar.title}
        </Typography>
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.72rem', lineHeight: 1.35 }}>
          {pillar.body}
        </Typography>
      </CardContent>
    </Card>
  );
}

// Phase C.1b — a single Career Mode "card," deliberately lighter-weight
// than PillarCard above (no bordered Card, no hover lift, smaller type)
// so this entire section reads as visually SUBORDINATE to the four
// COMMUNICATE/MEASURE/DEVELOP/UNDERSTAND product pillars — those are
// "what FFN does," this is "how you participate," and the phase brief is
// explicit that the two must not compete for visual weight. Text-only
// per mode, matching the phase's own explicit "keep this concise" +
// "do not claim unfinished systems are currently available" — every
// description below is written as the FFN experience/vision, not a
// promise every listed capability already exists today.
function CareerModeCard({ mode }) {
  const meta = CAREER_MODE_META[mode];
  return (
    <Box sx={{ textAlign: 'center', px: 1.5 }}>
      <Typography sx={{ fontSize: '1.5rem', mb: 0.5 }}>{meta.emoji}</Typography>
      <Typography sx={{ fontWeight: 800, color: '#c0d4e8', fontSize: '0.85rem', letterSpacing: '0.03em', mb: 0.5 }}>
        {meta.label.toUpperCase()}
      </Typography>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.1em', mb: 0.75 }}>
        {meta.tagline.toUpperCase()}
      </Typography>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem', lineHeight: 1.55, maxWidth: 200, mx: 'auto' }}>
        {meta.description}
      </Typography>
    </Box>
  );
}

export default function FFNLandingPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Phase C.2 — "Find Your Program" now routes to the genuinely PUBLIC
  // /programs directory instead of the authenticated /my-programs
  // management list (which only ever showed Programs the visitor
  // personally administers, never a general "find a program" experience
  // — see PublicProgramDirectoryPage.js's own header). No sign-in
  // required to browse it at all. "Create a Program" is unchanged: it
  // still navigates to the real Program-creation flow unconditionally —
  // the existing RequireAuth gate on that page (with Program-appropriate
  // copy, see CreateProgramPage.js) is what handles the logged-out case,
  // the same "existing auth convention" every other authenticated
  // destination in this app already relies on. currentUser is read only
  // to decide whether the supporting line below the buttons is worth
  // showing.
  const handleFindProgram = () => navigate('/programs');
  const handleCreateProgram = () => navigate('/my-programs/new');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'transparent' }}>
      <Navbar />

      <Hero onFindProgram={handleFindProgram} onCreateProgram={handleCreateProgram} />

      <Container maxWidth="lg" sx={{ pb: 4, flex: 1 }}>
        {!currentUser && (
          <Typography sx={{ textAlign: 'center', color: '#6a8aa8', fontSize: '0.8rem', mb: 2.5 }}>
            {/* Phase C.2 — "finding" a Program no longer requires an
                account at all (the /programs directory is fully public);
                only "creating" one still does. */}
            New here? Browse programs freely, or sign in / make a free account to create one.
          </Typography>
        )}

        {/* ── Five Pillars ──────────────────────────────────────────────────
            Phase C.1c — plain CSS grid instead of MUI's 12-unit <Grid>:
            12 does not divide evenly by 5, so getting exactly five equal
            columns at tablet-landscape/desktop needs gridTemplateColumns
            directly rather than fighting the 12-column system. Breakpoint
            behavior (this phase's own explicit reference points):
              xs   (phone)             -> 2 columns  ("2+2+1")
              sm   (tablet portrait)   -> 3 columns  ("3+2")
              md+  (tablet landscape,
                    ~1024px, and up)   -> 5 columns, ALL ON ONE ROW
            CSS grid columns always sum to exactly the container width, so
            this can never cause page-level horizontal overflow at any
            breakpoint — there is nothing to scroll. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.5,
          mb: { xs: 3, md: 3.5 },
        }}>
          {PILLARS.map(p => <PillarCard key={p.key} pillar={p} />)}
        </Box>

        {/* ── Find Your Place in Football (Career Modes) ───────────────────
            Phase C.1b — deliberately BETWEEN the pillars and the "where
            the platform is today" copy, per that phase's own layout
            instruction. Visually subordinate to PILLARS above (no
            bordered cards, muted palette, smaller type — see
            CareerModeCard's own header note) so the product pillars
            ("what FFN does") keep the primary visual weight and this row
            ("how you participate") reads as a secondary, explanatory
            section beneath them. Exactly four modes, Director/Admin
            deliberately never listed here — see utils/careerModes.js's
            own header.
            Phase C.1c — tightened (borderTop pt, heading mt/mb, tagline
            mb, Grid spacing, outer mb all reduced) so this section sits
            noticeably closer to the pillar row and appears higher on the
            page, without changing anything about WHICH four modes are
            shown or how switching/enabling them works. */}
        <Box sx={{ textAlign: 'center', mb: 3.5, pt: 0.5, borderTop: '1px solid #16202e' }}>
          <Typography sx={{ fontWeight: 900, letterSpacing: '0.08em', color: '#6a8aa8', fontSize: '0.8rem', mt: 1.5, mb: 0.5 }}>
            FIND YOUR PLACE IN FOOTBALL
          </Typography>
          <Typography sx={{ color: '#4a6080', fontSize: '0.8rem', mb: 1.75 }}>
            One account. Your football journey.
          </Typography>
          <Grid container spacing={1.5} justifyContent="center">
            {CAREER_MODES.map(mode => (
              <Grid item xs={6} sm={3} key={mode}>
                <CareerModeCard mode={mode} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── Where the platform is today ──────────────────────────────── */}
        <Box sx={{ maxWidth: 720, mx: 'auto', textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, color: '#e8f0f8', mb: 1 }}>
            Built for real programs, one piece at a time
          </Typography>
          <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', lineHeight: 1.6 }}>
            Football for Nerds already supports Program, Season, Division, and Team organization,
            athlete measurables and personal bests, and interactive coach Playbooks with
            responsibilities, reads, and teaching Moments. Training libraries, public results, and
            more of the platform are still being built — this page reflects where things stand
            today, not a finished product.
          </Typography>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
}
